import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';
import type {
  SyncLogDetail,
  SyncLogSummary,
  SyncLogChangeType,
  PaginatedResponse,
  SyncLogsResponse,
} from '@/types/api';

export const syncLogKeys = {
  all: ['sync-logs'] as const,
  list: (accountId: string, params?: SyncLogsParams) =>
    [...syncLogKeys.all, 'list', accountId, params] as const,
  summary: (syncLogId: string) =>
    [...syncLogKeys.all, 'summary', syncLogId] as const,
  details: (syncLogId: string, params?: SyncLogDetailsParams) =>
    [...syncLogKeys.all, 'details', syncLogId, params] as const,
};

interface SyncLogsParams {
  page?: number;
  limit?: number;
  status?: 'pending' | 'running' | 'completed' | 'failed';
  startDate?: string;
  endDate?: string;
}

export interface SyncLogDetailsParams {
  page?: number;
  limit?: number;
  changeType?: SyncLogChangeType;
}

export function useSyncLogs(accountId: string, params?: SyncLogsParams) {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', params.page.toString());
  if (params?.limit) queryParams.set('limit', params.limit.toString());
  if (params?.status) queryParams.set('status', params.status);
  if (params?.startDate) queryParams.set('startDate', params.startDate);
  if (params?.endDate) queryParams.set('endDate', params.endDate);

  const queryString = queryParams.toString();
  const url = `/api/v1/partner-accounts/${accountId}/sync-logs${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: syncLogKeys.list(accountId, params),
    queryFn: () => api.get<SyncLogsResponse>(url),
    enabled: !!accountId,
  });
}

/**
 * Fetch summary counts for a sync log (created, updated, unchanged, deleted, error).
 */
export function useSyncLogSummary(syncLogId: string | null) {
  return useQuery({
    queryKey: syncLogKeys.summary(syncLogId ?? ''),
    queryFn: () => api.get<SyncLogSummary>(`/api/v1/sync-logs/${String(syncLogId)}/summary`),
    enabled: !!syncLogId,
  });
}

/**
 * Fetch paginated sync log details with optional filtering by change type.
 */
export function useSyncLogDetails(
  syncLogId: string | null,
  params?: SyncLogDetailsParams
) {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.limit) queryParams.set('limit', String(params.limit));
  if (params?.changeType) queryParams.set('changeType', params.changeType);

  const queryString = queryParams.toString();
  const url = `/api/v1/sync-logs/${String(syncLogId)}/details${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: syncLogKeys.details(syncLogId ?? '', params),
    queryFn: () => api.get<PaginatedResponse<SyncLogDetail>>(url),
    enabled: !!syncLogId,
  });
}
